// src/app/core/shared/year.store.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

const LS_KEY = 't0_year_range_v1';

type YearRangeState = {
  year: number;
  displayMinYear: number;
};

export type Horizon = 5 | 10 | 15 | 20;

@Injectable({ providedIn: 'root' })
export class YearStore {
  // Default requirement: on load, horizon = 5 years -> year 2031
  readonly year$ = new BehaviorSubject<number>(2031);

  // For T0 timelines you can keep 2011; for your dashboard comparison you wanted baseline anchored at 2026.
  // We'll default to 2026 so the chart subtitle "Base (BAU)" matches your new expectation.
  readonly displayMinYear$ = new BehaviorSubject<number>(2026);

  constructor() {
    this.restore();
  }

  setYear(year: number) {
    const y = this.toYear(year);
    if (y === null) return;
    this.year$.next(y);
    this.persist();
  }

  setDisplayMinYear(year: number) {
    const y = this.toYear(year);
    if (y === null) return;
    this.displayMinYear$.next(y);
    this.persist();
  }

  setRange(minYear: number, maxYear: number) {
    const min = this.toYear(minYear);
    const max = this.toYear(maxYear);
    if (min === null || max === null) return;

    this.displayMinYear$.next(Math.min(min, max));
    this.year$.next(Math.max(min, max));
    this.persist();
  }

  // Convenience: map 5 10 15 -> 2031 2036 2041
setHorizon(h: Horizon) {
  const horizonToYearMap: Record<Horizon, number> = {
    5: 2031,
    10: 2036,
    15: 2041,
    20: 2046
  };

  this.setYear(horizonToYearMap[h]);
}

  // Use this from dashboard init to enforce your defaults on first load.
  // It won't override if user already has stored values unless force=true.
  ensureDefaults(opts?: { defaultMin?: number; defaultYear?: number; force?: boolean }) {
    const force = !!opts?.force;

    const defaultMin = this.toYear(opts?.defaultMin ?? 2026) ?? 2026;
    const defaultYear = this.toYear(opts?.defaultYear ?? 2031) ?? 2031;

    if (force) {
      this.displayMinYear$.next(defaultMin);
      this.year$.next(defaultYear);
      this.persist();
      return;
    }

    // If storage already set something, restore() already applied it.
    // We only enforce defaults when values look uninitialized/out of expected range.
    const curMin = this.displayMinYear$.value;
    const curYear = this.year$.value;

    if (!Number.isFinite(curMin) || curMin <= 0) this.displayMinYear$.next(defaultMin);
    if (!Number.isFinite(curYear) || curYear <= 0) this.year$.next(defaultYear);

    this.persist();
  }

  private toYear(v: any): number | null {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return Math.round(n);
  }

  private persist() {
    const state: YearRangeState = {
      year: this.year$.value,
      displayMinYear: this.displayMinYear$.value
    };
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(state));
    } catch {}
  }

  private restore() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<YearRangeState> | null;
      if (!parsed) return;

      const y = this.toYear(parsed.year);
      const min = this.toYear(parsed.displayMinYear);

      if (min !== null) this.displayMinYear$.next(min);
      if (y !== null) this.year$.next(y);
    } catch {}
  }
}